/**
 * Valida um conjunto de e-mails separados por vírgula.
 *
 * @param {string} emails - Os e-mails a serem validados, separados por vírgula.
 * @returns {boolean} `true` se algum e-mail for inválido; `false` se todos os e-mails forem válidos.
 */
function validarEmails(emails) {
  const listaEmails = emails.split(",");
  const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  for (const email of listaEmails) {
    if (!regexEmail.test(email.trim())) return true;
  }

  return false;
}

/**
 * Verifica se algum valor em um objeto FormData é indefinido, vazio, "null" ou "undefined".
 *
 * @param {FormData} formValues - O objeto FormData a ser verificadaao.
 * @returns {boolean} `true` se algum valor for inválido; `false` se todos os valores forem válidos.
 */
function verificarForm(formValues) {
  for (const [key, value] of formValues.entries()) {
    if (
      value === undefined ||
      value === "" ||
      value === "null" ||
      value === "undefined"
    ) {
      console.warn(`O campo ${key} está vazio.`);
      return true;
    }
  }
  return false;
}

/**
 * Verifica se um arquivo tem uma extensão permitida.
 *
 * @param {Array.<string>} extensoes - As extensões permitidas, sem o ponto (por exemplo, ["jpg", "png"]).
 * @param {string} arquivo - O nome do arquivo a ser verificado.
 * @returns {boolean} `true` se a extensão do arquivo não for permitida; `false` se for permitida.
 */
function verificarTipoArquivo(extensoes, arquivo) {
  for (const extensao of extensoes) {
    const extensaoRegex = new RegExp(`\\.${extensao}$`, "i");

    if (extensaoRegex.test(arquivo)) return false;
  }

  return true;
}

/**
 * Verifica se o tamanho de um arquivo está dentro dos limites especificados em megabytes, kilobytes ou bytes.
 *
 * @param {Object} options - Opções para a verificação de tamanho.
 * @param {File} options.arquivo - O arquivo a ser verificado.
 * @param {number} [options.mb=null] - O tamanho máximo permitido em megabytes.
 * @param {number} [options.kb=null] - O tamanho máximo permitido em kilobytes.
 * @param {number} [options.b=null] - O tamanho máximo permitido em bytes.
 * @returns {boolean} `true` se o arquivo excede o tamanho máximo permitido; `false` se está dentro dos limites.
 * @throws {Error} Se nenhum tamanho (mb, kb ou b) for especificado.
 */
function verificarTamanhoArquivo({ arquivo, mb = null, kb = null, b = null }) {
  var maxSizeInBytes;
  if (mb) maxSizeInBytes = mb * 1024 * 1024;
  if (kb) maxSizeInBytes = kb * 1024;
  if (b) maxSizeInBytes = b;

  if (!mb && !kb && !b) {
    console.error(
      "Defina o tamanho do arquivo na função verificarTamanhoArquivo."
    );
    return true;
  }

  if (arquivo > maxSizeInBytes) return true;

  return false;
}

/**
 * Valida um CNPJ utilizando o algoritmo de validação.
 *
 * @param {string} cnpj - O CNPJ a ser validado.
 * @returns {boolean} `true` se o CNPJ for inválido; `false` se for válido.
 */
function validaCnpj(cnpj) {
  const onlyNumbers = cnpj.replace(/\D/g, "");
  if (onlyNumbers.length !== 14) {
    return true;
  }

  if (/^(\d)\1+$/.test(onlyNumbers)) {
    return true;
  }

  let sum = 0;
  let position = 5;

  for (let i = 0; i < 12; i++) {
    sum += parseInt(onlyNumbers.charAt(i)) * position;
    position--;

    if (position === 1) {
      position = 9;
    }
  }

  const remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;

  sum = 0;
  position = 6;

  for (let i = 0; i < 13; i++) {
    sum += parseInt(onlyNumbers.charAt(i)) * position;
    position--;

    if (position === 1) {
      position = 9;
    }
  }

  var digit2 = sum % 11;

  digit2 = digit2 < 2 ? 0 : 11 - digit2;

  if (
    parseInt(onlyNumbers.charAt(12)) !== digit1 ||
    parseInt(onlyNumbers.charAt(13)) !== digit2
  ) {
    return true;
  }

  return false;
}

/**
 * Valida um CPF utilizando o algoritmo de validação.
 *
 * @param {string} cpf - O CPF a ser validado.
 * @returns {boolean} `true` se o CPF for inválido; `false` se for válido.
 */
function validaCpf(cpf) {
  const cleanCpf = cpf.replace(/[^\d]/g, "");

  if (cleanCpf.length !== 11) {
    return true;
  }

  const areAllDigitsEqual = cleanCpf
    .split("")
    .every((digit) => digit === cleanCpf[0]);
  if (areAllDigitsEqual) {
    return true;
  }

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
  }

  const remainder = sum % 11;
  const firstDigit = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cleanCpf.charAt(9)) !== firstDigit) {
    return true;
  }

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
  }

  const secondRemainder = sum % 11;
  const secondDigit = secondRemainder < 2 ? 0 : 11 - secondRemainder;

  return parseInt(cleanCpf.charAt(10)) !== secondDigit;
}

/**
 * Valida um e-mail.
 *
 * @param {string} email - O e-mail a ser validado.
 * @returns {boolean} `true` se o e-mail for inválido; `false` se o e-mail for válido.
 */
function validarEmail(email) {
  const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!regexEmail.test(email)) return true;

  return false;
}

/**
 * Formata erros no emissor.
 *
 * @param {Array.<string>} erros - Erros a serem formatados.
 * @returns {string} retorna html com os erros do emissor.
 */
function validaEmissor(erros) {
  const cssMotivos = `
    color: #74788d !important;
    font-weight: 500 !important;
    float: left !important;
    font-family: var(--bs-font-sans-serif) !important;
    font-size: 0.9rem !important;
    line-height: 1.5 !important;
    text-align: left;
  `;

  let motivos = "";

  erros.forEach((erro) => {
    if (erro.trim() !== "") {
      motivos += `
        <p style="${cssMotivos}">
          <i class="bx bxs-check-circle mr-2 text-orange" style="margin-right:5px !important"></i>
          ${erro}
        </p><br>
      `;
    }
  });

  return motivos;
}

/**
 * Valida uma data contra limites mínimos e máximos (opcionais).
 *
 * @param {string} valor - A data a ser validada no formato 'YYYY-MM-DD'.
 * @param {string|null} [min=null] - A data mínima permitida no formato 'YYYY-MM-DD'. Opcional.
 * @param {string|null} [max=null] - A data máxima permitida no formato 'YYYY-MM-DD'. Opcional.
 * @returns {boolean} `true` se a data for inválida ou fora dos limites; `false` se a data for válida e estiver dentro dos limites.
 * @throws {Error} Se algum valor não estiver no formato 'YYYY-MM-DD'.
 */
function validarDataMinMax(valor, min = null, max = null) {
  const regexData = /^\d{4}-\d{2}-\d{2}$/;

  if (
    !regexData.test(valor) ||
    (min && !regexData.test(min)) ||
    (max && !regexData.test(max))
  ) {
    console.warn("A data deve estar no formato YYYY-MM-DD.");
    return true;
  }

  const valorDate = new Date(valor);
  const minDate = new Date(min);
  const maxDate = new Date(max);

  if (max && valorDate > maxDate) return true;
  if (min && valorDate < minDate) return true;

  return false;
}

/**
 * Verifica se uma senha atende aos requisitos mínimos de segurança.
 *
 * @param {string} senha - A senha a ser verificada.
 * @returns {string|boolean} Uma string HTML contendo mensagens de erro para os requisitos não atendidos;
 *                           retorna `false` se todos os requisitos forem satisfeitos.
 */
function verificarSenha(senha) {
  // Estilo CSS inline para as mensagens
  const cssRequisitos = `
    color: #74788d !important;
    font-weight: 500 !important;
    float: left !important;
    font-family: var(--bs-font-sans-serif) !important;
    font-size: 0.9rem !important;
    line-height: 1.5 !important;
  `;
  const iconStyle = `margin-right: 5px !important`;

  const requisitos = [
    {
      regex: /[a-z]/,
      mensagem: `<p style="${cssRequisitos}"><i class="bx bxs-check-circle mr-2 text-orange" style="${iconStyle}"></i>A senha deve ter pelo menos uma letra minúscula.</p>`
    },
    {
      regex: /[A-Z]/,
      mensagem: `<p style="${cssRequisitos}"><i class="bx bxs-check-circle mr-2 text-orange" style="${iconStyle}"></i>A senha deve ter pelo menos uma letra maiúscula.</p>`
    },
    {
      regex: /\d/,
      mensagem: `<p style="${cssRequisitos}"><i class="bx bxs-check-circle mr-2 text-orange" style="${iconStyle}"></i>A senha deve ter pelo menos um número.</p>`
    },
    {
      regex: /[!@#$%^&~*+=_(),.?":{}|/<>]/,
      mensagem: `<p style="${cssRequisitos}"><i class="bx bxs-check-circle mr-2 text-orange" style="${iconStyle}"></i>A senha deve ter pelo menos um caractere especial.</p>`
    },
    {
      regex: /^.{8,}$/,
      mensagem: `<p style="${cssRequisitos}"><i class="bx bxs-check-circle mr-2 text-orange" style="${iconStyle}"></i>A senha deve ter pelo menos oito caracteres ao todo.</p>`
    }
  ];

  let resultado = '';
  for (const requisito of requisitos) {
    if (!requisito.regex.test(senha)) {
      resultado += requisito.mensagem + '<br>';
    }
  }

  return resultado === '' ? false : resultado;
}

const Validation = {
  validarEmails,
  validarEmail,
  verificarForm,
  verificarTipoArquivo,
  verificarTamanhoArquivo,
  validaCnpj,
  validaCpf,
  validaEmissor,
  validarDataMinMax,
  verificarSenha,
};
export default Validation;
